import { Injectable, inject } from "@angular/core";
import { round } from "../../utils/gis-helpers";
import { ConfigService } from "../config/config.service";
import { DatabaseService } from "../database/database.service";
import { MAP_PROCESSES } from "../database/process-db-names";

@Injectable({
  providedIn: "root",
})
export class MapObjectsService {
  private database = inject(DatabaseService);
  private configCtrl = inject(ConfigService);

  /** Cursor selection is available only when zoomed in beyond this level. */
  public readonly cursorSelectionMinZoom = 10;

  public readonly airspace = 0;
  public readonly airport = 1;
  public readonly procedures = 15;
  public readonly navaid = 16;
  public readonly notam = 10;
  public readonly obstacle = 6;
  public readonly webcam = 4;
  public readonly weather = 5;

  public async getDetailMapObject(id: string, type: string): Promise<any> {
    const config = await this.configCtrl.configP;
    const d = new Date();
    const res = await this.database.namedQuery("mapObjectDetail", {
      id,
      toolId: config.mapToolId,
    });

    // this replace sucks
    const dataObj = [];

    const validUntil = res[0].validUntil;

    // added that line, as it lets webcams disappear.. :/
    const showAlways: boolean = true;

    if (validUntil == null || parseFloat(validUntil) > d.getTime() || showAlways) {
      for (let i = 0; i < res.length; i++) {
        let data = res[i].value.replace(/{\/2}/g, "//");
        if (data.startsWith(String.fromCharCode(65279))) {
          data = data.substring(1, data.length);
        }

        // what an ugly hack:
        const withoutId: any = JSON.parse(data);
        const withId: any = withoutId;
        withId.data["id"] = id;

        dataObj.push(withId);
      }

      // DEV
      if (dataObj[0].script.length > 0) {
        return this.database.selectProcesses([...MAP_PROCESSES], dataObj[0].script[0].query).then((r) => {
          if (r.length > 0) {
            const item = r[0].value;
            return { dataObj, item };
          }
        });
      } else {
        return { dataObj, item: "" };
      }
    }
  }

  public async getMapObjects(coordinate: number[], zoom: number, types: number[]): Promise<MapObjectInterface[]> {
    if (zoom <= this.cursorSelectionMinZoom) return [];

    const config = await this.configCtrl.configP;
    const d = new Date();

    // Convert zoom to resolution for bounding box calculation
    const EQUATORIAL_RES_Z0 = 156543.03;
    const resolution = EQUATORIAL_RES_Z0 / Math.pow(2, zoom);

    const earthRadius = 3440.0652187;
    const nauticalMile = 1852;
    const margin: number = 40;

    const ry_ = Math.asin((resolution * margin) / (earthRadius * nauticalMile)) * margin;
    const rx_ =
      Math.asin((resolution * margin) / (earthRadius * nauticalMile * Math.cos((coordinate[1] / 180) * Math.PI))) * 32;

    const min = [round(coordinate[0] - rx_, 3), round(coordinate[1] - ry_, 3)];
    const max = [round(coordinate[0] + rx_, 3), round(coordinate[1] + ry_, 3)];

    const data = await this.database.namedQuery("mapGeometriesInBoundsByType", {
      minX: min[0],
      maxX: max[0],
      minY: min[1],
      maxY: max[1],
      types,
    });
    const listOfObj = [];

    for (let i = 0; i < data.length; i += 1) {
      const it = data[i];
      const res = JSON.parse(it.geoJson);
      switch (res.geometry.type) {
        case "Point":
          listOfObj.push(it.id);
          break;

        case "LineString":
          if (this.pointInPolygon(res.geometry.coordinates, coordinate)) {
            listOfObj.push(it.id);
          }
          break;

        case "Polygon":
          if (this.pointInPolygon(res.geometry.coordinates[0], coordinate)) {
            listOfObj.push(it.id);
          }
          break;
      }
    }

    if (listOfObj.length > 0) {
      // disabled by OV: 20.12.22: as outdated
      // webcams should be selectable
      // +
      // " and effectiveFrom < " +
      // d.getTime().toString() +
      // " and validUntil > " +
      // d.getTime().toString() +

      const data = await this.database.namedQuery("mapObjectsByIds", {
        ids: listOfObj,
        toolId: config.mapToolId,
      });

      if (data.length > 0) {
        return data
          .map((item) => ({
            name: item.content,
            type: item.icon,
            text: "",
            id: item.id,
            priority: this.getPriorityFromIcon(item.icon),
          }))
          .sort((a, b) => {
            return b.priority - a.priority;
          });
      } else {
        return [];
      }
    } else {
      return [];
    }
  }

  public getNavObjects(coordinate: [number, number]): NavObjectInterface[] {
    this.navobj.push(this.navobj[0]);
    this.navobj = this.navobj.slice(1, this.navobj.length);
    for (let i = 0; i < this.navobj.length; i++) {
      if (this.navobj[i].name === "User") {
        this.navobj[i].coordinate = coordinate;
        break;
      }
    }
    return this.navobj;
  }

  private navobj = [
    {
      name: "LOWI",
      type: "ahp",
      text: "",
      id: 1,
      priority: 2,
      coordinate: [11.343, 47.26] as [number, number],
    },
    {
      name: "INN",
      type: "loc",
      text: "",
      id: 2,
      priority: 2,
      coordinate: [11.398, 47.23] as [number, number],
    },
    {
      name: "M3",
      type: "wp",
      text: "",
      id: 2,
      priority: 2,
      coordinate: [11.416, 47.24] as [number, number],
    },
    {
      name: "User",
      type: "wp",
      text: "",
      id: 2,
      priority: 2,
      coordinate: [0, 0] as [number, number],
    },
  ];

  private getPriorityFromIcon(icon: string): number {
    switch (icon) {
      case "weather":
        return 5;
      case "airport":
        return 4;
      case "camera":
        return 3;
      case "notam":
        return 2;
      default:
        return 1;
    }
  }

  private pointInPolygon(polygon: Array<Array<number>>, point: Array<number>): boolean {
    // If the point lies on the polygon boundary or on a vertex of the polygon, the point will be stated as 'IN'.
    let deltaAngle = 0;
    const angle = [];

    // Get intersections
    for (let qq = 1; qq < polygon.length; qq++) {
      const angle1 = Math.atan2(polygon[qq][1] - point[1], polygon[qq][0] - point[0]);
      const angle2 = Math.atan2(polygon[qq - 1][1] - point[1], polygon[qq - 1][0] - point[0]);
      if (angle1 - angle2 < -Math.PI) {
        angle.push(2 * Math.PI + (angle1 - angle2));
      } else if (angle1 - angle2 > Math.PI) {
        angle.push(-2 * Math.PI + (angle1 - angle2));
      } else {
        angle.push(angle1 - angle2);
      }

      deltaAngle = deltaAngle + angle[angle.length - 1];
    }

    if (Math.round(Math.abs(deltaAngle) * 1000) / 1000 == 6.283) {
      return true;
    } else {
      return false;
    }
  }
}

export interface MapObjectInterface {
  name: string;
  type: string;
  text: string;
  id: number;
  priority: number;
}

export interface NavObjectInterface {
  name: string;
  type: string;
  text: string;
  id: number;
  priority: number;
  coordinate: [number, number];
}

export interface MapObjectDetailInterface {
  text: string;
  img: string;
  name: string;
  geoJson: string;
}
